import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import Breadcrums from "../../common/breadcrumbs";
import { SWAL_SETTINGS, INPUT_LENGTH_40, EXPERTISE, languageSpoken } from "../../../../utils/Constants";
import {
  handleServerValidations,
  DT,
  globalLoader,
} from "../../../../utils/commonfunction";
import * as Yup from "yup";
import CustomError from "../../../../utils/customError";
import { useTranslation } from "react-i18next";
import { Add } from "../../services/psychic.services"; // Changed import from Edit to Add
import { TagPicker, Tooltip, Whisper } from "rsuite";
import PhoneInput from "../../../../utils/PhoneInput";
import { COMMON_INPUT_VALIDATION, EMAIL_VALIDATION, PHONE_VALIDATION } from "../../../../utils/commonValidations";

// Data for TagPickers
const expertiseData = Object.entries(EXPERTISE).map(([value, label]) => ({ label, value }));
const languageData = languageSpoken.map(lang => ({ label: lang.name, value: lang.code }));

const AstrologerAdd = (props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [previewimage, setPreviewImage] = useState("");
  const [saveType, setSaveType] = useState("");
  const [authMethod, setAuthMethod] = useState('email'); // 'email' or 'phone'

  const breadcrumbs = [
    { title: t("link_dashboard"), url: "/admin/dashboard" },
    {
      title: t("Astrologer"),
      url: "/admin/astrologer-management/astrologer/list/1",
    },
    { title: t("Add"), url: "" }, // Changed "Edit" to "Add"
  ];

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .max(INPUT_LENGTH_40, DT(t("max_length_error_dynamic"), [INPUT_LENGTH_40]))
      .matches(/^[a-zA-Z\s]+$/, "Name can only contain alphabets and spaces")
      .concat(COMMON_INPUT_VALIDATION)
      .required("Name is required"),
    email: Yup.string().when([], {
      is: () => authMethod === 'email',
      then: (schema) => schema.concat(EMAIL_VALIDATION).required("Email is required"),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
    // phone_number: Yup.string().when([], {
    //     is: () => authMethod === 'phone_number',
    //     then: (schema) => schema.concat(PHONE_VALIDATION).required("Phone number is required"),
    //     otherwise: (schema) => schema.notRequired().nullable(),
    // }),
    phone_number: Yup.string().when([], {
      is: () => authMethod === 'phone_number',
      then: (schema) => schema
        .required("Phone number is required")
        .matches(/^[6-9]\d{9}$/, "Phone number must be a valid 10-digit Indian number"),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
    expertise: Yup.array()
    .min(1, "At least one expertise is required")
    .of(
      Yup.object({
        expertise_type: Yup.string().required("Expertise type is required"),
        price_per_minute: Yup.number()
          .typeError("Price must be a number")
          .integer("Price must be an integer")
          .positive("Price must be a positive number")
          .max(10000, "Price must not exceed 10,000")
          .required("Price per minute is required"),
      })
    ),
  online_puja: Yup.array().of(
        Yup.object().shape({
          puja_type: Yup.string()
            .max(20, "Puja name must be at most 20 characters")
            .matches(/^[\w\s'-]+$/, "Puja name must contain only valid characters")
            .nullable(), // Can be empty
          price_per_minute: Yup.number()
            .typeError("Price must be a number")
            .integer("Price must be an integer")
            .positive("Price must be a positive number")
            .max(10000, "Price must not exceed 10,000")
            .nullable(), // Can be empty
        })
        .test(
          "puja-fields-required-together",
          "Both Puja name and Price per minute must be provided together, or both left empty.",
          function (value) {
            const { puja_type, price_per_minute } = value;
  
            const nameFilled = !!puja_type && puja_type.trim() !== "";
            const priceFilled = typeof price_per_minute === "number" && price_per_minute !== null && price_per_minute !== "";
  
            const priceValid =
              typeof price_per_minute === "number" &&
              price_per_minute > 0 &&
              price_per_minute <= 10000 &&
              Number.isInteger(price_per_minute);
  
            // Both empty
            if (!nameFilled && !priceFilled) return true;
  
            // Both filled
            if (nameFilled && priceFilled) return priceValid;
  
            // One is filled and not the other
            return false;
          }
        )
      ),
    language_spoken: Yup.array()
      .min(1, "At least one language must be provided")
      .required("Language spoken is required"),
    experience: Yup.number().typeError("Experience must be a number").min(0, "Experience cannot be negative").max(100, "Experience seems too high can not exceed 100").required("Experience is required"),
    commission: Yup.number().typeError("Commission must be a number").min(0, "Commission cannot be negative").max(100, "Commission cannot exceed 100").required("Commission is required"),
    profile_image: Yup.mixed()
      .required("Image is required")
      .test("fileSize", "Image size is too large (max 5MB)", (value) => value && value.size <= 5 * 1024 * 1024)
      .test("fileType", "Unsupported file format", (value) => value && ["image/jpg", "image/jpeg", "image/png", "image/webp", "image/gif"].includes(value.type)),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phone_number: "",
      selected_expertise: [], // For TagPicker state
      expertise: [], // For submission data
      online_puja: [{ puja_type: '', price_per_minute: '' }],
      language_spoken: [],
      experience: "",
      commission: "",
      profile_image: null,
    },
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      globalLoader(true);
      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("login_with", authMethod);
      if (authMethod === 'email') {
        formData.append("email", values.email);
      } else {
        formData.append("phone_number", values.phone_number);
      }
      formData.append("expertise", JSON.stringify(values.expertise));
      formData.append("experience", values.experience);
      formData.append("commission", values.commission);
      formData.append("language_spoken", JSON.stringify(values.language_spoken));
      formData.append("online_puja", JSON.stringify(values.online_puja.filter(p => p.puja_type && p.price_per_minute))); // Filter out empty puja objects
      if (values.profile_image) {
        formData.append("profile_image", values.profile_image);
      }
      
      Add(formData) // Changed from Edit to Add
        .then((response) => {
          setSubmitting(false);
          globalLoader(false);
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
            if (saveType === "Save") {
              navigate(`/admin/astrologer-management/astrologer/list/1`);
            } else {
                // Reset form partially if user wants to add another
                formik.resetForm({values: { ...formik.initialValues, language_spoken: values.language_spoken, experience: values.experience, commission: values.commission, profile_image: null }});
                setPreviewImage("");
            }
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        })
        .catch((error) => {
          globalLoader(false);
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        });
    },
  });

  // Sync TagPicker selection with the expertise array that holds price info
  useEffect(() => {
    const updatedExpertise = formik.values.selected_expertise.map(expertValue => {
        const existing = formik.values.expertise.find(e => e.expertise_type === expertValue);
        return existing || { expertise_type: expertValue, price_per_minute: '' };
    });
    // This will only keep expertise that are currently selected
    formik.setFieldValue('expertise', updatedExpertise);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.selected_expertise]);


  const handlePriceChange = (expertiseType, price) => {
    const updatedExpertise = formik.values.expertise.map(exp =>
      exp.expertise_type === expertiseType
        ? { ...exp, price_per_minute: price }
        : exp
    );
    formik.setFieldValue('expertise', updatedExpertise);
  };

  const addPuja = () => {
    formik.setFieldValue('online_puja', [
      ...formik.values.online_puja,
      { puja_type: '', price_per_minute: '' },
    ]);
  };

  const removePuja = (index) => {
      const currentPujas = formik.values.online_puja;
      if (currentPujas.length > 1) {
          const updatedPujas = currentPujas.filter((_, i) => i !== index);
          formik.setFieldValue("online_puja", updatedPujas);
      } else {
          // Do nothing or reset the last one to empty if removal attempted
          formik.setFieldValue("online_puja", [{ puja_type: "", price_per_minute: "" }]);
      }
  };


  console.log("Formik Values:", formik.values); // Debugging line to check form values
  console.log("errors:", formik.errors); // Debugging line to check form errors
  
  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <form onSubmit={formik.handleSubmit}>
        <div className="row row-sm">
          <div className="col-lg-12 col-md-12 animation_fade">
            <div className="card custom-card">
              <div className="card-body">
                <div>
                  <h6 className="main-content-label mb-3">
                    {t("Add Astrologer")}
                  </h6>
                </div>
                <div className="row row-sm">
                  <div className="col-md-6 form-group">
                    <label htmlFor="name" className="text-left d-flex">
                      {t("Name")}:<span className="requirestar">*</span>
                    </label>
                    <input
                      name="name"
                      id="name"
                      type="text"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.name}
                      className="form-control"
                      placeholder={t("Enter name")}
                    />
                    <CustomError name="name" form={formik} />
                  </div>

                  <div className="col-md-6 form-group">
                      <label className="text-left d-flex">
                          Registration Method:<span className="requirestar">*</span>
                      </label>
                      <div className="btn-group">
                          <button type="button" className={`btn ${authMethod === 'email' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setAuthMethod('email')}>
                              Use Email
                          </button>
                          <button type="button" className={`btn ${authMethod === 'phone_number' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setAuthMethod('phone_number')}>
                              Use Phone Number
                          </button>
                      </div>
                  </div>

                  {authMethod === 'email' && (
                    <div className="col-md-6 form-group">
                      <label htmlFor="email" className="text-left d-flex">
                        {t("label_email")}:<span className="requirestar">*</span>
                      </label>
                      <input
                        name="email"
                        id="email"
                        type="email"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.email}
                        className="form-control"
                        placeholder={t("placeholder_email")}
                      />
                      <CustomError name="email" form={formik} />
                    </div>
                  )}

                  {authMethod === 'phone_number' && (
                    <div className="col-md-6 form-group">
                      <label className="text-left d-flex" htmlFor="phone_number">
                        {t("label_phone_number")}:<span className="requirestar">*</span>
                      </label>
                      {/* <PhoneInput
                        name="phone_number"
                        id="phone_number"
                        placeholder={t('placeholder_phone_number')}
                        onChange={(e) => formik.setFieldValue('phone_number', e?.phone_number)}
                        onBlur={formik.handleBlur}
                        value={formik.values.phone_number}
                        className="form-control"
                      /> */}
                      <input
                        name="phone_number"
                        id="phone_number"
                        type="text"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.phone_number}
                        className="form-control"
                        placeholder={t("Enter phone number")}
                      />
                      <CustomError name="phone_number" form={formik} />
                    </div>
                  )}

                  <div className="col-md-6 form-group">
                    <label htmlFor="experience" className="text-left d-flex">
                      {t("Experience (in Years)")}:<span className="requirestar">*</span>
                    </label>
                    <input
                      name="experience"
                      id="experience"
                      type="number"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.experience}
                      className="form-control"
                      placeholder={t("Enter years of experience")}
                    />
                    <CustomError name="experience" form={formik} />
                  </div>

                  <div className="col-md-6 form-group">
                    <label htmlFor="commission" className="text-left d-flex">
                      {t("Commission (%)")}:<span className="requirestar">*</span>
                    </label>
                    <input
                      name="commission"
                      id="commission"
                      type="number"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.commission}
                      className="form-control"
                      placeholder={t("Enter commission percentage")}
                    />
                    <CustomError name="commission" form={formik} />
                  </div>
                  
                  <div className="col-md-12">
                    <hr />
                    <h6>{t("Expertise and Pricing")}</h6>
                  </div>

                  <div className="col-md-6 form-group">
                      <label htmlFor="expertise" className="text-left d-flex">
                          {t("Select Expertise")}:<span className="requirestar">*</span>
                      </label>
                      <TagPicker
                          data={expertiseData}
                          block
                          value={formik.values.selected_expertise}
                          onChange={(value) => formik.setFieldValue('selected_expertise', value || [])}
                          onBlur={formik.handleBlur}
                          name="selected_expertise"
                          placeholder="Select one or more expertise"
                      />
                      {/* FIX: Only render CustomError if the error is a string to prevent crashes */}
                      {formik.touched.selected_expertise && typeof formik.errors.expertise === 'string' ? (
                          <div className="text-danger d-flex text-left">{formik.errors.expertise}</div>
                      ) : null}
                  </div>

                  <div className="col-md-6"></div>

                  {formik.values.expertise.map((exp, index) => (
                    <div key={exp.expertise_type} className="col-md-6 form-group">
                        <label className="text-left d-flex">
                            Price for {EXPERTISE[exp.expertise_type] || exp.expertise_type} (per minute):
                            <span className="requirestar">*</span>
                        </label>
                        <input
                            type="number"
                            name={`expertise[${index}].price_per_minute`}
                            value={exp.price_per_minute}
                            onChange={(e) => handlePriceChange(exp.expertise_type, e.target.value)}
                            onBlur={formik.handleBlur}
                            className="form-control"
                            placeholder="Enter price per minute"
                        />
                        {/* <CustomError name={`expertise[${index}].price_per_minute`} form={formik} /> */}
                        {/* Access individual error */}
                        {formik.errors.expertise &&
                        formik.errors.expertise[index] &&
                        formik.errors.expertise[index].price_per_minute && (
                          <div className="text-danger">
                            {formik.errors.expertise[index].price_per_minute}
                          </div>
                        )}
                    </div>
                  ))}

                  <div className="col-md-12">
                      <hr/>
                      <h6>Online Puja Services</h6>
                  </div>

                 {formik.values.online_puja.map((puja, index) => {
                    const pujaErrors = formik.errors.online_puja && formik.errors.online_puja[index] ? formik.errors.online_puja[index] : {};
                    const touchedPuja = formik.touched.online_puja && formik.touched.online_puja[index];

                    return (
                      <div key={index} className="row align-items-center mb-2">
                        <div className="col-md-4 form-group mb-0">
                          <label className="text-left d-flex">Puja Type:</label>
                          <input
                            type="text"
                            name={`online_puja[${index}].puja_type`}
                            value={puja.puja_type}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            className="form-control"
                            placeholder="Enter puja type"
                          />
                          <span className="form-text text-danger d-inline-block" style={{ minHeight: "1em" }}>
                            {touchedPuja?.puja_type && pujaErrors?.puja_type ? pujaErrors.puja_type : <>&nbsp;</>}
                          </span>
                        </div>

                        <div className="col-md-4 form-group mb-0">
                          <label className="text-left d-flex">Price per Minute:</label>
                          <input
                            type="number"
                            name={`online_puja[${index}].price_per_minute`}
                            value={puja.price_per_minute}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            className="form-control"
                            placeholder="Enter price per minute"
                          />
                          <span className="form-text text-danger d-inline-block" style={{ minHeight: "1em" }}>
                            {/* Normal price_per_minute field error */}
                            {touchedPuja?.price_per_minute && pujaErrors?.price_per_minute ? pujaErrors.price_per_minute : <>&nbsp;</>}
                            {/* Error from object-level test */}
                            {typeof pujaErrors === "string" && touchedPuja
                              ? pujaErrors
                              : ""}
                          </span>
                        </div>

                        <div className="col-md-3 form-group d-flex align-items-center mb-0">
                          {/* Remove/Add buttons */}
                          {formik.values.online_puja.length > 1 && (
                            <button type="button" className="btn btn-danger mr-2" onClick={() => removePuja(index)}>Remove</button>
                          )}
                          {index === formik.values.online_puja.length - 1 && (
                            <button type="button" className="btn btn-success" onClick={addPuja}>Add More</button>
                          )}
                        </div>
                      </div>
                    );
                  })}
  
                  <div className="col-md-12"><hr/></div>

                  <div className="col-md-6 form-group">
                    <label htmlFor="language_spoken" className="text-left d-flex">
                      {t("Language Spoken")}:<span className="requirestar">*</span>
                    </label>
                    <TagPicker
                        data={languageData}
                        block
                        value={formik.values.language_spoken}
                        onChange={(value) => formik.setFieldValue('language_spoken', value || [])}
                        placeholder="Select one or more languages"
                    />
                    <CustomError name="language_spoken" form={formik} />
                  </div>
                  
                  <div className="col-lg-6 form-group">
                    <label htmlFor="profile_image" className="text-left d-flex">
                      {t("Profile Image")}:<span className="requirestar">*</span>
                      <Whisper placement="top" trigger="hover" speaker={<Tooltip>{t("image_support_tooltip")}</Tooltip>}>
                        <span className="field-more-info mt-1 ms-1 cp">?</span>
                      </Whisper>
                    </label>
                    <input
                      className="form-control imgInput"
                      id="profile_image"
                      name="profile_image"
                      accept="image/*"
                      type="file"
                      onChange={(event) => {
                        const file = event.currentTarget.files[0];
                        formik.setFieldValue("profile_image", file);
                        setPreviewImage(file ? URL.createObjectURL(file) : "");
                      }}
                    />
                    <CustomError name="profile_image" form={formik} />
                    {previewimage && (
                      <div className="mt-2"><img src={previewimage} style={{ height: "100px", borderRadius: "8px" }} alt="Profile Preview"/></div>
                    )}
                  </div>

                  <div className="col-12 mt-4">
                    <button onClick={() => setSaveType("Save")} className="btn btn-success mr-2" type="submit" disabled={formik.isSubmitting}>
                      {t("btn_submit")}
                    </button>
                    <button className="btn btn-secondary" type="button" onClick={() => navigate(-1)}>
                      {t("btn_cancel")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default AstrologerAdd;


